import { MarketingOrgConfigBlock, MarketingOrgEntityConfig } from "../../../../../api/v1/organizations/types";
import { TemplateField } from "../../../../../types/template";
import { isSelectOption } from "../../../../libs/utils";

/** Config keys from marketingDefaultOrgConfig.[property|unit] */
const ORG_SELECT_CONFIG_KEYS = ["amenities", "pointsOfInterest"] as const;

type OrgSelectConfigKey = (typeof ORG_SELECT_CONFIG_KEYS)[number];

/** Stable form field names (keeps existing customFields keys working). */
const FIELD_NAME_BY_CONFIG_KEY: Record<OrgSelectConfigKey, string> = {
  amenities: "amenities",
  pointsOfInterest: "pointsOfInterest",
};

const FIELD_META_BY_CONFIG_KEY: Record<
  OrgSelectConfigKey,
  { order: number; isMulti: boolean; defaultLabel: string; colSpan?: number }
> = {
  amenities: { order: 5, isMulti: true, defaultLabel: "Amenities", colSpan: 24 },
  pointsOfInterest: { order: 6, isMulti: true, defaultLabel: "Points of Interest", colSpan: 24 },
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeRules = (rules: Record<string, unknown> | undefined): TemplateField["rules"] => {
  if (!rules || typeof rules !== "object") return {};

  const next: Record<string, unknown> = { ...rules };
  if (next.required === true) {
    next.required = "This field is required";
  } else if (next.required === false) {
    delete next.required;
  }
  if (next.min == null) delete next.min;
  if (next.max == null) delete next.max;

  return next as TemplateField["rules"];
};

const buildOptionsFromBlock = (block: MarketingOrgConfigBlock) => {
  const formOptions = (block.formField?.options || []).filter(isSelectOption);
  const formOptionByLabel = new Map(
    formOptions.map((option) => [String(option.label).trim().toLowerCase(), String(option.value)]),
  );

  const data = block.data || [];
  if (data.length > 0) {
    return data
      .filter((item) => item?.isActive !== false && item?.label)
      .map((item) => {
        const label = String(item.label);
        const value = formOptionByLabel.get(label.trim().toLowerCase()) || slugify(label);
        return { label, value };
      });
  }

  return formOptions.map((option) => ({
    label: String(option.label),
    value: String(option.value),
  }));
};

/** Options for Parking Types select from org `marketingDefaultOrgConfig.[entity].parkingTypes`. */
export const buildOrgParkingTypeOptions = (
  entityConfig?: MarketingOrgEntityConfig | null,
): { label: string; value: string }[] => {
  const block = entityConfig?.parkingTypes;
  if (!block) return [];
  return buildOptionsFromBlock(block);
};

export const buildOrgMarketingSelectFields = (
  entityConfig?: MarketingOrgEntityConfig | null,
): TemplateField[] => {
  if (!entityConfig) return [];

  return ORG_SELECT_CONFIG_KEYS.reduce<TemplateField[]>((fields, configKey) => {
    const block = entityConfig[configKey];
    if (!block) return fields;

    const options = buildOptionsFromBlock(block);
    const meta = FIELD_META_BY_CONFIG_KEY[configKey];
    const formField = block.formField;

    fields.push({
      label: formField?.label || meta.defaultLabel,
      name: FIELD_NAME_BY_CONFIG_KEY[configKey],
      type: formField?.type || "select",
      section: (formField?.section as string) || "details",
      order: typeof formField?.order === "number" ? formField.order : meta.order,
      colSpan: typeof formField?.colSpan === "number" ? formField.colSpan : meta.colSpan,
      isMulti: formField?.isMulti ?? meta.isMulti,
      options,
      // Amenities: select all org options by default when entity has no saved marketingData amenities.
      default: configKey === "amenities" ? options : meta.isMulti ? [] : null,
      rules: normalizeRules(formField?.rules as Record<string, unknown> | undefined),
    });

    return fields;
  }, []);
};
